import * as React from "react";
import { AnalyticsStore } from "bernie-core";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { UitkExpandoLink, UitkExpandoLinkTypeSize } from "uitk-react-expando";
import { UitkPrimaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import {
  expandoCollapsedTitle,
  expandoExpandedTitle,
} from "components/flexComponents/Hotels/views/HotelsViews/HotelUtils";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { withStores } from "stores";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";

interface HotelImageCardProps {
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  hotel: Hotel;
  analytics: AnalyticsStore;
}

const HotelCard = withStores("analytics")((props: HotelImageCardProps) => {
  const { hotel, model, context } = props;
  const { callToAction, contentRecipe, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  /* covered in e2e test */
  /* istanbul ignore next */
  const expandoLinkAnalytics: AnalyticsConfig = {
    id: "LP.Hotels.HotelImageHalf.ExpandoLink",
    callback: (theId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkPrimaryButton);

  return (
    <UitkCard>
      <UitkCardContentSection padded={false}>
        <UitkLayoutGrid columns={2}>
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <LazyLoadedImage src={hotelImageUrl} alt={hotel.hotelName} context={context} />
          </UitkFigure>
          <UitkLayoutFlex direction="column" className="all-x-padding-two all-y-padding-two">
            <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
              {hotelName}
            </UitkHeading>
            <UitkRating rating={hotelStarRating} icon={ratingIcon(context.locale)} className={context.site.brand} />
            <UitkText size={300} className="truncate-lines-2">
              {hotelFormattedFullAddress}
            </UitkText>
            <PriceLockup
              hotel={hotel}
              align={UitkPriceLockupAlign.LEFT}
              className="all-t-margin-three"
              priceA11y={new HotelPriceLockup(hotel, formatText)}
              hasGetRatesText
            />
          </UitkLayoutFlex>
        </UitkLayoutGrid>

        <UitkCardLink>
          <TrackedLink
            data-testid={`hotelcard-link-${hotel.hotelName}`}
            aria-label={hotelName}
            moduleName="hotels"
            rfrr="card"
            rel={getHotelLinkRel(callToAction)}
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
          />
        </UitkCardLink>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <UitkExpandoLink
          type={UitkExpandoLinkTypeSize.TYPE_200}
          isVisible={false}
          expandoTitle={expandoCollapsedTitle(contentRecipe)}
          expandedLabel={expandoExpandedTitle(contentRecipe)}
          analytics={expandoLinkAnalytics}
        >
          <UitkLayoutFlex direction="column" className="all-y-padding-three">
            <DistanceOrRating model={model} hotel={hotel} className="all-b-margin-three" context={context} />
            <DescriptionOrReview model={model} hotel={hotel} />
            <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
          </UitkLayoutFlex>

          <UitkCardContentSection padded={false} border="top" className="all-t-padding-three">
            <ValueProposition hotel={hotel} className="all-b-margin-three" />
            <ButtonTracked
              href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
              isFullWidth
              moduleName="LP.hotels.hotelImageHalf"
              rfrr="expandoExpanded.select"
            >
              {formatText("hotelsViews.expando.select")}
            </ButtonTracked>
          </UitkCardContentSection>
        </UitkExpandoLink>
      </UitkCardContentSection>
    </UitkCard>
  );
});

export default HotelCard;
